/** Trading-hours schedule helpers. */
const WD = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const fmtCache = new Map();
function localParts(date, tz) {
    let f = fmtCache.get(tz);
    if (!f) {
        f = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
        fmtCache.set(tz, f);
    }
    let day = 0;
    let h = 0;
    let m = 0;
    for (const p of f.formatToParts(date)) {
        if (p.type === 'weekday')
            day = WD[p.value] ?? 0;
        else if (p.type === 'hour')
            h = Number(p.value);
        else if (p.type === 'minute')
            m = Number(p.value);
    }
    return { day, minutes: h * 60 + m };
}
const toMin = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
};
export function isMarketOpen(hours, date = new Date()) {
    if (!hours || 'always' in hours)
        return true;
    const { day, minutes } = localParts(date, hours.tz);
    const prev = (day + 6) % 7;
    for (const s of hours.sessions) {
        const o = toMin(s.open);
        const c = toMin(s.close);
        if (o < c) {
            if (s.days.includes(day) && minutes >= o && minutes < c)
                return true;
        }
        else {
            if (s.days.includes(day) && minutes >= o)
                return true;
            if (s.days.includes(prev) && minutes < c)
                return true;
        }
    }
    return false;
}
/** Next swap charge time strictly after `now`, given the UTC hour charges happen at. */
export function nextSwapAt(now, hourUtc) {
    const t = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hourUtc, 0, 0));
    if (t.getTime() <= now.getTime())
        t.setUTCDate(t.getUTCDate() + 1);
    return t;
}
/**
 * When the market next opens, or null if it's open now (or never opens within `horizonDays`).
 * Scans in 30-minute steps, then narrows to the minute; sessions in practice start on whole minutes.
 */
export function nextMarketOpen(hours, date = new Date(), horizonDays = 8) {
    if (isMarketOpen(hours, date))
        return null;
    const STEP = 30 * 60_000;
    const start = Math.floor(date.getTime() / 60_000) * 60_000;
    for (let t = start + STEP; t <= start + horizonDays * 86_400_000; t += STEP) {
        if (!isMarketOpen(hours, new Date(t)))
            continue;
        let lo = t - STEP; // closed
        let hi = t; // open
        while (hi - lo > 60_000) {
            const mid = lo + Math.floor((hi - lo) / 120_000) * 60_000;
            if (isMarketOpen(hours, new Date(mid)))
                hi = mid;
            else
                lo = mid;
        }
        return new Date(hi);
    }
    return null;
}
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** "Mon–Fri" for consecutive days, else "Mon, Wed, Fri". */
function dayRange(days) {
    const d = [...days].sort((a, b) => a - b);
    const consecutive = d.every((x, i) => i === 0 || x === d[i - 1] + 1);
    return consecutive && d.length > 2 ? `${DAY_NAMES[d[0]]}–${DAY_NAMES[d.at(-1)]}` : d.map((x) => DAY_NAMES[x]).join(', ');
}
/** "America/New_York" → "New York". */
export const tzCity = (tz) => (tz.split('/').pop() ?? tz).replace(/_/g, ' ');
/**
 * Trading hours in words: "24/7", "Mon–Fri 09:30–16:00 New York time", or for a session that runs past
 * midnight every day "Sun 18:00 to Fri 17:00 New York time, with a daily break from 17:00 to 18:00".
 */
export function describeHours(hours) {
    if (!hours || 'always' in hours)
        return '24/7';
    const zone = `${tzCity(hours.tz)} time`;
    const [only] = hours.sessions;
    if (hours.sessions.length === 1 && only && toMin(only.close) <= toMin(only.open)) {
        const d = [...only.days].sort((a, b) => a - b);
        const consecutive = d.every((x, i) => i === 0 || x === d[i - 1] + 1);
        if (consecutive) {
            const span = `${DAY_NAMES[d[0]]} ${only.open} to ${DAY_NAMES[(d.at(-1) + 1) % 7]} ${only.close} ${zone}`;
            return only.open === only.close ? `${span}, without a daily break` : `${span}, with a daily break from ${only.close} to ${only.open}`;
        }
    }
    // Sessions on the same days (e.g. a lunch break) read as one line: "Mon–Fri 09:15–12:00 and 13:00–16:30".
    const byDays = new Map();
    for (const s of hours.sessions) {
        const k = dayRange(s.days);
        byDays.set(k, [...(byDays.get(k) ?? []), `${s.open}–${s.close}${toMin(s.close) <= toMin(s.open) ? ' (next day)' : ''}`]);
    }
    return `${[...byDays].map(([days, times]) => `${days} ${times.join(' and ')}`).join('; ')} ${zone}`;
}
//# sourceMappingURL=hours.js.map